import '@sorare/core/src/polyfills';
import './persistsQueryStringParameters/index.ts';
import { captureException, withScope } from '@sentry/core';

import '@sorare/core/src/style/style.css';
import './remove-child-workaround.js';
import { useCallback, useMemo, useReducer } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router';

import { ErrorBoundary } from '@sorare/error-boundary';
import { DeviceFingerPrintProvider } from '@sorare/core/src/contexts/deviceFingerprint/Provider';
import { FeatureFlagsProvider } from '@sorare/core/src/contexts/featureFlags/Provider';
import { GraphqlProvider } from '@sorare/core/src/contexts/graphql/Provider';
import { IntlProvider } from '@sorare/core/src/contexts/intl/Provider';
import { withProfiler } from '@sorare/core/src/contexts/sentry/sentry';
import { SnackNotificationProvider } from '@sorare/core/src/contexts/snackNotification/Provider';
import { TMProvider } from '@sorare/core/src/contexts/tm/Provider';
import { useBasenameLocale } from '@sorare/core/src/hooks/useBasenameLocale';
import { formatError, reactErrorHandler } from '@sorare/core/src/lib/error';
import { withFFProvider } from '@sorare/core/src/lib/featureFlags';
import { HandledError } from '@sorare/core/src/routing/HandledError';

import {
  EphemeralLink,
  isEphemeralLink,
} from './components/EphemeralLink/index.tsx';
import { ViteErrorHandler } from './components/ViteErrorHandler/index.tsx';
import { routeObjects } from './routeObjects.tsx';
import { createBrowserRouter } from './sentry.ts';

const RootProviders = ({
  locale,
  children,
  basename,
}: Pick<ComponentProps<typeof IntlProvider>, 'locale'> & {
  children: ReactNode;
  basename?: string;
}) => {
  return (
    <DeviceFingerPrintProvider>
      <TMProvider>
        <ErrorBoundary
          fallbackRender={error => (
            <HandledError
              message={error.message}
              code={500}
              urls={'urls' in error ? (error.urls as any) : undefined}
            />
          )}
        >
          <GraphqlProvider
            locale={locale}
            basename={basename}
            routeObjects={routeObjects}
          >
            {children}
          </GraphqlProvider>
        </ErrorBoundary>
      </TMProvider>
    </DeviceFingerPrintProvider>
  );
};

const ApolloClientRouter = ({
  basename,
  locale,
  setLocale,
}: {
  basename?: string;
} & Pick<ComponentProps<typeof IntlProvider>, 'locale' | 'setLocale'>) => {
  // We need to wait for apollo client to be initialized before we can create
  // the browser router, as this will launch requests.
  const router = useMemo(
    () =>
      createBrowserRouter(routeObjects, {
        basename,
      }),
    [basename]
  );

  return (
    <IntlProvider locale={locale} setLocale={setLocale}>
      <SnackNotificationProvider>
        <HelmetProvider>
          <RouterProvider
            router={router}
            key={basename}
            unstable_onError={(error, info) => {
              const { location, params, errorInfo } = info;
              // info.componentStack is present for render errors (from componentDidCatch)
              // info is different (or null/undefined) for loader/action errors
              if (errorInfo?.componentStack) {
                // render error — already handled by react onCaughtError, skip
                return;
              }
              // ignore handled errors
              const { handleError = false } = formatError(error) || {};
              if (handleError) {
                return;
              }
              withScope(scope => {
                scope.setExtra('location', location);
                scope.setExtra('params', params);
                captureException(error, {
                  mechanism: {
                    handled: false,
                    type: `auto.function.sentry.error_handler.router_provider`,
                  },
                });

                if (import.meta.env.MODE === 'development') {
                  // eslint-disable-next-line no-console
                  console.error(error, info);
                }
              });
            }}
          />
        </HelmetProvider>
      </SnackNotificationProvider>
    </IntlProvider>
  );
};

const Root = () => {
  const { basename, locale, setLocale } = useBasenameLocale();

  return (
    <RootProviders locale={locale} basename={basename}>
      <ApolloClientRouter
        key={basename}
        basename={basename}
        locale={locale}
        setLocale={setLocale}
      />
    </RootProviders>
  );
};

const EphemeralLinkOrRoot = () => {
  const [, forceUpdate] = useReducer(() => ({}), {});

  const navigateTo = useCallback((path: string) => {
    window.history.replaceState({}, '', path);
    forceUpdate();
  }, []);

  // eslint-disable-next-line no-restricted-properties
  return isEphemeralLink(window.location.pathname) ? (
    <RootProviders locale="en">
      <IntlProvider locale="en" setLocale={() => {}}>
        <EphemeralLink navigateTo={navigateTo} />
      </IntlProvider>
    </RootProviders>
  ) : (
    <Root />
  );
};

const RootWithProfiler = withProfiler(EphemeralLinkOrRoot);

const container = document.getElementById('root');
const root = createRoot(container!, {
  onCaughtError: reactErrorHandler('caught'),
  onUncaughtError: reactErrorHandler('uncaught'),
  onRecoverableError: reactErrorHandler('recoverable'),
});
const App = () => (
  <FeatureFlagsProvider>
    <ViteErrorHandler />
    <RootWithProfiler />
  </FeatureFlagsProvider>
);
const FFProvider = withFFProvider()(App);
root.render(<FFProvider />);

// unregister any service worker left-over (see https://gitlab.com/sorare/frontend/-/merge_requests/3852)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister();
    });
  });
}
